import {
  Button,
  Icon,
  IconButton,
  MenuItem,
  TextField,
} from "@material-ui/core";
import { CashlessRepository } from "Infrastructure/repositories/api/CashlessRepository";
import { PedidoPos } from "Infrastructure/interfaces/PedidoMeep";
import { WalletRepository } from "Infrastructure/repositories/api/WalletRepository";
import { useAppInsights } from "Infrastructure/repositories/appInsights/AppInsights";
import { getOrderRepository } from "../../../Infrastructure/repositories/localStorage/getOrderRepository";
import BottomModal from "components/bottomModal/BottomModal";
import { OrderLog } from "domains/order/agreggates/order/Order";
import { WalletRechargeUseCase } from "modules/cashless/application/useCases/WalletRechargeUseCase";
import { IWalletRechargeModel } from "modules/payment/domain/models/IWalletTransaction";
import React, { useCallback, useEffect, useState } from "react";
import { AppConfigurationData } from "../../../Infrastructure/models/AppConfigurationData";
import { MeepOrderRepository } from "../../../Infrastructure/repositories/api/OrderRepository";
import { CefSharpConfigRepository } from "../../../Infrastructure/services/cefSharpService/local/CefSharpConfigRepository";
import { useKiosk } from "../../../application/contexts/kiosk/KioskContext";
import { useSession } from "../../../application/contexts/session/SessionContext";
import { useUi } from "../../../application/contexts/ui/UIContext";
import styles from "./KioskParameters.module.scss";
import { LocalStorageWalletRepository } from "Infrastructure/repositories/localStorage/LocalStorageWalletRepository";
import { LocalStorageCashlessOfflineRepository } from "Infrastructure/repositories/localStorage/LocalStorageCashlessOfflineRepository";
import { LocalStorageSyncPendingsRepository } from "Infrastructure/repositories/localStorage/LocalStorageSyncPendingsRepository";
import {
  ReactNativePaymentRepository,
  getSoftExpressPagSeguroSubAcquirerData,
} from "Infrastructure/services/reactNativeServices/ReactNativePaymentService";
import { SitefMetadata, getDeviceInfo, transaction } from "modules/reactNativeBridge/ReactNativeBridge";
import { TransactionType } from "modules/payment/domain/models/TransactionType";
import { KioskRepository } from "Infrastructure/repositories/api/KioskRepository";
import { useEvents } from "modules/events/presentation/UseEvents";
import { useAdmin } from "application/contexts/admin/adminContext";
import { useCatalogStore } from "application/contexts/catalog/CatalogStore";
import {
  OrientationType,
  useScreenStore,
} from "application/contexts/screen/ScreenStore";

const configRepository = CefSharpConfigRepository();
const orderRepository = MeepOrderRepository();
const orderRepositoryLocalStorage = getOrderRepository();
const cashlessRepository = CashlessRepository();
const walletRepository = LocalStorageWalletRepository();
const cashlessOfflineRepository = LocalStorageCashlessOfflineRepository();
const syncPendingOrdersRepository = LocalStorageSyncPendingsRepository();
const kioskRepository = KioskRepository();

export const KioskParameters = () => {
  const { kiosk } = useKiosk();
  const { addLog } = useAppInsights();
  const { sessionIsOpen, closeSession, openSession } = useSession();
  const { toast, showLoading, hideLoading } = useUi();
  const { orientation } = useScreenStore();
  const { selectedOperator } = useAdmin();
  const { events, loading: loadingEvents, loginEvent, loginLoading } = useEvents(
    kiosk?.localId,
    Boolean(kiosk?.worksWithEvents)
  );
  const { forceSync } = useCatalogStore();
  const [selectedEvent, setSelectedEvent] = useState("");

  const [configData, setConfigData] = useState<AppConfigurationData | null>(
    null
  );
  const [pedidosPendentesLista, setPedidosPendentesLista] = useState<
    PedidoPos[]
  >([]);
  const [pedidosPendentes, setPedidosPendentes] = useState<number>();
  const [selectedPort, setSelectedPort] = useState("");
  const [recargasPendentes, setRecargasPendentes] = useState<OrderLog[]>([]);
  const [pendentRecordConfirmation, setPendentRecordConfirmation] = useState<
    PedidoPos[]
  >([]);
  const [pendentWalletRecharge, setPendentWalletRecharge] = useState<
    IWalletRechargeModel[]
  >([]);
  const [openModalPendente, setOpenModalPendente] = useState(false);
  const [openModalRecargaPendente, setOpenModalRecargaPendente] =
    useState(false);
  const [openModalConfirmacaoPendente, setOpenModalConfirmacaoPendente] =
    useState(false);
  const [openModalWalletRechargePendente, setOpenModalWalletRechargePendente] =
    useState(false);
  const [tabsVisible, setTabsVisible] = useState(true);
  const [deviceSerial, setDeviceSerial] = useState<string>("");

  useEffect(() => {
    setSelectedPort(localStorage.getItem("@weighning-port") ?? "");
    syncPendingOrdersRepository.getStoragePendentOrder().then((response) => {
      setPedidosPendentesLista(response);
      setPedidosPendentes(response.length);
    });

    cashlessOfflineRepository
      .getNotSincronizedOfflineRecharges()
      .then((response) => {
        setPendentRecordConfirmation(response);
      });

    walletRepository.getPendentWalletRecharge().then((response) => {
      setPendentWalletRecharge(response);
    });

    orderRepositoryLocalStorage.getAllPendingRecharge().then((response) => {
      setRecargasPendentes(response);
    });

    const fetchDeviceSerial = async () => {
      try {
        const deviceInfo = await getDeviceInfo();
        if (deviceInfo?.serialNumber) {
          setDeviceSerial(deviceInfo.serialNumber);
        }
      } catch (error) {
        console.error("Erro ao obter serial do dispositivo:", error);
      }
    };

    fetchDeviceSerial();
  }, []);

  const handleSession = useCallback(async () => {
    try {
      showLoading();
      if (sessionIsOpen) {
        await closeSession();
      } else {
        await openSession();
      }
    } finally {
      hideLoading();
    }
  }, [closeSession, hideLoading, openSession, sessionIsOpen, showLoading]);

  const handleSelectEvent = useCallback(
    async (eventId: string) => {
      const operadorId = selectedOperator?.id;

      if (!eventId || !kiosk?.localId || !operadorId) {
        return;
      }

      try {
        setSelectedEvent("login");
        const loginResponse = await loginEvent({
          eventId,
          operadorId,
        });
        if (!loginResponse) {
          toast("Não foi possível fazer login no evento.", "error");
          setSelectedEvent("");
          return;
        }
        await forceSync(eventId);
        setSelectedEvent(eventId);
      } catch (error) {
        toast("Não foi possível autenticar o evento.", "error");
        setSelectedEvent("");
      }
    },
    [forceSync, kiosk, loginEvent, selectedOperator, toast]
  );

  const onClickSyncoOrderById = useCallback(
    async (id: string) => {
      if (kiosk) {
        showLoading();
        try {
          const response = await orderRepository.syncOrdersByIdAsync(
            kiosk?.localId,
            id
          );

          if (response.length) {
            toast(
              response.length +
              " de " +
              pedidosPendentes +
              " foram sincronizados",
              "success"
            );
          }

          setPedidosPendentesLista((prev) =>
            prev.filter((item) => item.pedidoPOSId !== id)
          );
          setPedidosPendentes((prev) => (prev ?? 1) - 1);
        } catch (error) {
          toast("Não foi possivel sincronizar", "error");
        } finally {
          hideLoading();
        }
      }
    },
    [kiosk, showLoading, toast, pedidosPendentes, hideLoading]
  );

  const onClickConfirmOrderById = useCallback(
    async (id: string) => {
      if (kiosk) {
        showLoading();
        try {
          const response = await cashlessRepository.confirmPendingRecord([id]);

          toast(
            response.ordersId.length +
            " de " +
            pendentRecordConfirmation.length +
            " foram confirmados",
            "success"
          );
        } catch (error) {
          toast("Não foi possivel confirmar", "error");
        } finally {
          hideLoading();
        }
      }
    },
    [kiosk, showLoading, toast, pendentRecordConfirmation.length, hideLoading]
  );

  const onClickConfirmWalletRecharge = useCallback(
    async (walletRecharge: IWalletRechargeModel) => {
      if (kiosk) {
        showLoading();
        try {
          const walletService = WalletRepository();
          const walletTransaction = await WalletRechargeUseCase(
            walletService,
            walletRecharge
          );

          const newList =
            await walletRepository.removeWalletPendingRechargeFromStorageQueueAsync(
              walletRecharge.orderId
            );
          setPendentWalletRecharge(newList);
          const orderLog = {
            id: walletRecharge.orderId,
            walletRechargeResult: walletTransaction,
            isSynchronized: true,
          };

          addLog("Wallet", { message: "Recarga confirmada", orderLog });

          toast("Recarga confirmada", "success");

          setOpenModalWalletRechargePendente(false);
        } catch (error) {
          addLog("Wallet", {
            orderLog: {
              message: "Falha ao confirmar recarga",
              id: walletRecharge.orderId,
            },
          });
          toast("Não foi possivel confirmar", "error");
        } finally {
          hideLoading();
        }
      }
    },
    [kiosk, showLoading, addLog, toast, hideLoading]
  );

  const obterConfiguracoes = useCallback(async () => {
    setConfigData(await configRepository.getConfigAsync());
  }, []);

  useEffect(() => {
    obterConfiguracoes();
  }, [obterConfiguracoes]);

  const selectComWegining = useCallback((value: string) => {
    localStorage.setItem("@weighning-port", value);
    setSelectedPort(value);
  }, []);

  const itemView = useCallback((label: string, value?: string) => {
    return (
      <div
        style={{
          margin:
            orientation === OrientationType.LANDSCAPE ? "10px 0" : "30px 0",
        }}
      >
        <b>{label}:</b> {value}
      </div>
    );
  }, []);

  const openConfig = useCallback(async () => {
    try {
      showLoading();
      const paymentRepository = ReactNativePaymentRepository();

      let cnpj_automacao = "28302604000101";

      const DEFAULT_CONFIG: SitefMetadata = {
        // empresaSitef: '00000000',
        // CNPJ_CPF: '47504905000162',
        // enderecoSitef: "192.168.0.68",
        // comExterna: "0",
        // tokenRegistroTls: "1234-5678-9012-3456",
        //prod
        empresaSitef: kiosk?.softExpressStoreCode ?? kiosk?.softExpressStoreCodeMeepSub ?? "",
        //tokenRegistroTls: "3328-9181-5198-9057", // "0269-2825-3325-9708", // "8226-9036-1068-5474", // kiosk.transireToken ?? "",
        tokenRegistroTls: kiosk?.softExpressToken ?? "", //?? "4816-4536-1060-5453",// ?? "5169-7583-3769-5927",// "1158-0483-1099-9343", //"0431-5207-6255-5749",
        enderecoSitef: "tls-prod.fiservapp.com:443",
        CNPJ_CPF: kiosk?.softExpressCnpj ?? kiosk?.softExpressCnpjMeepSub ?? "",
        //cnpj_automacao: "28302604000101", // kiosk?.softExpressCnpjMeepSub ,
        cnpj_automacao: cnpj_automacao, // kiosk?.softExpressCnpjMeepSub ,
        comExterna: "4",
        dadosSubAdqui: kiosk?.softExpressIsPagSeguroSubAcquirer ? getSoftExpressPagSeguroSubAcquirerData(kiosk?.softExpressPagSeguroSubAcquirerData ?? {}) : undefined,
        terminalSitef: kiosk?.kioskName ?? "",
      };


      //const DEFAULT_CONFIG: SitefMetadata = {
      //  empresaSitef: kiosk?.softExpressStoreCodeMeepSub ?? "",
      //  tokenRegistroTls: kiosk?.softExpressToken ?? "4816-4536-1060-5453",// ?? "5169-7583-3769-5927", //"2859-1114-7770-8290",// ?? "0431-5207-6255-5749",
      //  enderecoSitef: "tls-prod.fiservapp.com:443",
      //  CNPJ_CPF: kiosk?.softExpressCnpj ?? kiosk?.softExpressCnpjMeepSub ?? "",
      //  cnpj_automacao: "28302604000101",
      //  dadosSubAdqui: getSoftExpressPagSeguroSubAcquirerData(
      //    kiosk?.softExpressPagSeguroSubAcquirerData ?? {}
      //  ),
      //  terminalSitef: kiosk?.kioskName ?? "",
      //};

      console.log("DEFAULT_CONFIG: ", DEFAULT_CONFIG);
      await paymentRepository?.openConfig?.(DEFAULT_CONFIG);
    } catch (error) {
      toast("Não foi possível abrir a configuração", "error");
      console.error("Erro ao abrir configuração:", error);
    } finally {
      hideLoading();
    }
  }, [kiosk, showLoading, hideLoading, toast]);

  const toggleTabs = useCallback(async () => {
    try {
      if (window.RNBridge) {
        const newVisibility = !tabsVisible;
        await window.RNBridge.callNative("toggleTabs", {
          visible: newVisibility,
        });
        setTabsVisible(newVisibility);
        toast(
          `Abas de teste ${newVisibility ? "exibidas" : "ocultadas"}`,
          "success"
        );
      } else {
        toast("Bridge não disponível", "error");
      }
    } catch (error) {
      toast("Erro ao alternar abas de teste", "error");
      console.error("Erro ao ocultar/exibir tabs:", error);
    }
  }, [tabsVisible, toast]);

  const handleGerarTokenFiserv = useCallback(async () => {
    try {
      showLoading();
      const resultado = await kioskRepository.gerarTokenFiservAsync();
      await kioskRepository.syncAsync();
      await transaction({
        amount: 1,
        typeTransaction: TransactionType.credit,
        token: resultado.Token,
        aquirer: 'msitef',
        metadata: {
          empresaSitef: kiosk?.softExpressStoreCode ?? kiosk?.softExpressStoreCodeMeepSub ?? "",
          tokenRegistroTls: resultado.Token,
          enderecoSitef: "tls-prod.fiservapp.com:443",
          CNPJ_CPF: kiosk?.softExpressCnpj ?? kiosk?.softExpressCnpjMeepSub ?? "",
          cnpj_automacao: "28302604000101",
          comExterna: "4",
          terminalSitef: kiosk?.kioskName ?? "",
        },
      });
      toast("Token gerado e transação de inicialização iniciada", "success");
    } catch (error) {
      toast("Erro ao gerar token Fiserv", "error");
      console.error("Erro ao gerar token Fiserv:", error);
    } finally {
      hideLoading();
    }
  }, [kiosk, showLoading, hideLoading, toast]);

  const handleSimularVenda = useCallback(async () => {
    try {
      showLoading();
      await transaction({
        amount: 1,
        typeTransaction: TransactionType.credit,
        token: kiosk?.softExpressToken ?? "",
        aquirer: 'msitef',
        metadata: {
          empresaSitef: kiosk?.softExpressStoreCode ?? kiosk?.softExpressStoreCodeMeepSub ?? "",
          tokenRegistroTls: kiosk?.softExpressToken ?? "",
          enderecoSitef: "tls-prod.fiservapp.com:443",
          CNPJ_CPF: kiosk?.softExpressCnpj ?? kiosk?.softExpressCnpjMeepSub ?? "",
          cnpj_automacao: "28302604000101",
          comExterna: "4",
          terminalSitef: kiosk?.kioskName ?? "",
        },
      });
      toast("Transação de teste iniciada", "success");
    } catch (error) {
      toast("Erro ao iniciar transação de teste", "error");
      console.error("Erro ao simular venda:", error);
    } finally {
      hideLoading();
    }
  }, [kiosk, showLoading, hideLoading, toast]);

  return (
    <div id={styles.KioskParameters}>
      <div>
        {itemView("Id do local", kiosk?.localId)}
        {itemView("Nome do local", kiosk?.localName)}
        {itemView("Nome do equipamento", kiosk?.kioskName)}
        {itemView("Id do equipamento", configData?.equipamentId)}
        {itemView("Endereço MAC", configData?.macAddress)}
        {itemView("Serial do dispositivo", deviceSerial)}
      </div>

      {!!pedidosPendentes ? (
        <div
          className={styles.pendentItem}
          onClick={() => setOpenModalPendente(true)}
        >
          <b>!</b>
          {pedidosPendentes} pedido(s) pendente(s).
        </div>
      ) : (
        <div className={styles.item}>
          <Icon>check</Icon>
          Nenhum pedido pendente.
        </div>
      )}
      {recargasPendentes.length > 0 ? (
        <div
          className={styles.pendentItem}
          onClick={() => setOpenModalRecargaPendente(true)}
        >
          <b>!</b>
          {recargasPendentes.length} recarga(s) pendente(s).
        </div>
      ) : (
        <div className={styles.item}>
          <Icon>check</Icon>
          Nenhuma recarga pendente.
        </div>
      )}
      {pendentRecordConfirmation.length > 0 ? (
        <div
          className={styles.pendentItem}
          onClick={() => setOpenModalConfirmacaoPendente(true)}
        >
          <b>!</b>
          Existem confirmações de gravação pendentes.
        </div>
      ) : (
        <div className={styles.item}>
          <Icon>check</Icon>
          Nenhuma gravação pendente.
        </div>
      )}
      {pendentWalletRecharge.length > 0 ? (
        <div
          className={styles.pendentItem}
          onClick={() => setOpenModalWalletRechargePendente(true)}
        >
          <b>!</b>
          Existem recargas Wallet pendentes.
        </div>
      ) : (
        <div className={styles.item}>
          <Icon>check</Icon>
          Nenhuma recarga Wallet pendente.
        </div>
      )}
      <TextField
        select
        value={selectedPort}
        onChange={(ev) => selectComWegining(ev.target.value)}
        className={styles.select}
        variant="outlined"
      >
        {Array.from({ length: 19 }, (_, i) => i + 1).map((it, key) => (
          <MenuItem value={`COM${it}`} key={key}>
            Porta serial balança: <b>&nbsp;{`COM${it}`}</b>
          </MenuItem>
        ))}
      </TextField>
      {kiosk?.worksWithEvents && (
        <TextField
          select
          value={selectedEvent}
          disabled={loginLoading}
          onChange={async (ev) => {
            const value = ev.target.value as string;

            handleSelectEvent(value);
          }}
          className={styles.select}
          variant="outlined"
          label="Selecione o evento"
        >
          <MenuItem value="" disabled>
            Selecione o evento
          </MenuItem>
          {loadingEvents && (
            <MenuItem value="loading" disabled>
              Carregando eventos...
            </MenuItem>
          )}
          {!loadingEvents &&
            events.map((event) => (
              <MenuItem value={event.id} key={event.id}>
                {event.name}
              </MenuItem>
            ))}
          {loginLoading && (
            <MenuItem value="login" disabled>
              Autenticando evento...
            </MenuItem>
          )}
        </TextField>
      )}
      <div className={styles.footer}>
        <Button
          onClick={handleSession}
          variant="contained"
          style={{
            background: !sessionIsOpen ? "#399C54" : "var(--primary)",
            border: `3px solid ${!sessionIsOpen ? "#399C54" : "var(--primary)"
              }`,
          }}
        >
          {sessionIsOpen ? "Fechar caixa" : "Abrir caixa"}
        </Button>

        {!kiosk?.softExpressIsPagSeguroSubAcquirer && <Button
          onClick={openConfig}
          variant="contained"
          style={{
            backgroundColor: "#5856D6",
            color: "#FFF",
            border: `3px solid #5856D6`,
          }}
        >
          Abrir Configuração
        </Button>
        }
        {/*<Button
          onClick={toggleTabs}
          variant="contained"
          style={{
            backgroundColor: tabsVisible ? "#FF9500" : "#34C759",
            color: "#FFF",
            border: `3px solid ${tabsVisible ? "#FF9500" : "#34C759"}`,
          }}
        >
          {tabsVisible ? "Ocultar Abas de Teste" : "Exibir Abas de Teste"}
        </Button>*/}

        <Button
          onClick={handleGerarTokenFiserv}
          variant="contained"
          style={{
            backgroundColor: "#007AFF",
            color: "#FFF",
            border: `3px solid #007AFF`,
          }}
        >
          Gerar Token Fiserv
        </Button>

        <Button
          onClick={handleSimularVenda}
          variant="contained"
          style={{
            backgroundColor: "#34C759",
            color: "#FFF",
            border: `3px solid #34C759`,
          }}
        >
          Simular Venda
        </Button>

        <Button
          style={{
            backgroundColor: "#FFF",
            color: "var(--primary)",
            border: `3px solid var(--primary)`,
          }}
          onClick={() => {
            window.close();
          }}
          variant="contained"
        >
          Encerrar aplicação
        </Button>
      </div>
      <div></div>
      <BottomModal
        showModal={openModalPendente}
        onClickClose={() => setOpenModalPendente(false)}
      >
        <div>
          {pedidosPendentesLista.map((item, index) => (
            <div>
              <span className={styles.pendentTitle}>Pedidos pendentes</span>
              <div key={index} className={styles.pendentListItem}>
                <div>{item.codigoPedido}</div>
                <div>
                  {item.dataPedido &&
                    new Date(item.dataPedido).toLocaleString()}
                </div>
                <div>R${item.valorTotal.toFixed(2)}</div>
                {item.pedidoPOSId && (
                  <IconButton
                    onClick={() =>
                      onClickSyncoOrderById(item.pedidoPOSId ?? "")
                    }
                  >
                    <Icon>sync</Icon>
                  </IconButton>
                )}
              </div>
            </div>
          ))}
        </div>
      </BottomModal>
      <BottomModal
        showModal={openModalRecargaPendente}
        onClickClose={() => setOpenModalRecargaPendente(false)}
      >
        <div>
          {recargasPendentes.map((item, index) => (
            <div>
              <span className={styles.pendentTitle}>Gravações pendentes</span>
              <div key={index} className={styles.pendentListItem}>
                <div>{item.friendlyId}</div>
                <div>{item.customer?.tag}</div>
                <div>
                  {item.createdAt && new Date(item.createdAt).toLocaleString()}
                </div>
                <div>R${item.totalValue.toFixed(2)}</div>
              </div>
            </div>
          ))}
        </div>
        <p style={{ padding: "0 16px", textAlign: "center" }}>
          Vá para a tela inicial da recarga e aproxime os cartões com recargas
          pendentes para concluir a gravação do novo saldo, ou use uma POS.
        </p>
      </BottomModal>
      <BottomModal
        showModal={openModalConfirmacaoPendente}
        onClickClose={() => setOpenModalConfirmacaoPendente(false)}
      >
        <div>
          {pendentRecordConfirmation.map((item, index) => (
            <div>
              <span className={styles.pendentTitle}>
                Confirmações de gravação pendentes
              </span>
              <div key={index} className={styles.pendentListItem}>
                <div>{item.codigoPedido}</div>
                <div>
                  {item.dataPedido &&
                    new Date(item.dataPedido).toLocaleString()}
                </div>
                <div>R${item.valorTotal.toFixed(2)}</div>
                {item.pedidoPOSId && (
                  <IconButton
                    onClick={() =>
                      onClickConfirmOrderById(item.pedidoPOSId ?? "")
                    }
                  >
                    <Icon>sync</Icon>
                  </IconButton>
                )}
              </div>
            </div>
          ))}
        </div>
      </BottomModal>
      <BottomModal
        showModal={openModalWalletRechargePendente}
        onClickClose={() => setOpenModalWalletRechargePendente(false)}
      >
        <div>
          {pendentWalletRecharge.map((item, index) => (
            <div>
              <span className={styles.pendentTitle}>
                Confirmações de recargas Wallet pendentes
              </span>
              <div key={index} className={styles.pendentListItem}>
                <div>{item.orderId}</div>
                <div>{item?.date && new Date(item.date).toLocaleString()}</div>
                <div>R${item.value.toFixed(2)}</div>
                <IconButton onClick={() => onClickConfirmWalletRecharge(item)}>
                  <Icon>sync</Icon>
                </IconButton>
              </div>
            </div>
          ))}
        </div>
      </BottomModal>
    </div>
  );
};
